import { firstToLowerCase, RectOption, getPoint } from '../../common/utils/helper';
import { NiceInterval } from '../../chart/axis/axis-helper';
import { DataManager, DataUtil } from '@syncfusion/ej2-data';
import { DataPoint } from '../utils/helper';
import { animationMode, getValue, isNullOrUndefined } from '@syncfusion/ej2-base';
import { getSeriesColor } from '../../common/model/theme';
import { PathOption, Rect, measureText, Size } from '@syncfusion/ej2-svg-base';
import { Data } from '../../common/model/data';
/**
 * To render Chart series
 */
export class RangeSeries extends NiceInterval {
    constructor(range) {
        super();
        this.dataSource = range.dataSource;
        this.xName = range.xName;
        this.yName = range.yName;
        this.query = range.query;
        this.xMin = Infinity;
        this.xMax = -Infinity;
        this.yMin = Infinity;
        this.yMax = -Infinity;
        this.labels = [];
        this.indexLabels = {};
    }
    /**
     * To render light weight and data manager process.
     *
     * @param {RangeNavigator} control - RangeNavigator instance.
     * @returns {void}
     */
    renderChart(control) {
        let dataSource;
        let query;
        this.seriesLength = 0;
        control.rangeSlider.points = [];
        const allSeries = control.series;
        if (allSeries.length) {
            allSeries.map((series) => {
                dataSource = series.dataSource || control.dataSource;
                query = series.query || control.query;
                series.points = [];
                this.processDataSource(dataSource, query, control, series);
            });
        }
        else {
            this.processDataSource(control.dataSource, control.query, control);
        }
    }
    processDataSource(dataSource, query, control, series) {
        if (!(dataSource instanceof DataManager) && !isNullOrUndefined(dataSource) && isNullOrUndefined(query)) {
            this.dataManagerSuccess({ result: dataSource, count: dataSource.length }, control, series);
            return;
        }
        control.dataModule = new Data(dataSource, query);
        const dataManager = control.dataModule.getData(control.dataModule.generateQuery().requiresCount());
        dataManager.then((e) => this.dataManagerSuccess(e, control, series));
    }
    /**
     * data manager process calculated here.
     *
     * @param {Object} e - The data manager result object.
     * @param {Object} e.result - The result of the data manager process.
     * @param {number} e.count - The count of items in the result.
     * @param {RangeNavigator} control - The RangeNavigator control.
     * @param {RangeNavigatorSeries} series - Optional parameter representing the series data.
     * @returns {void}
     */
    dataManagerSuccess(e, control, series) {
        const viewData = e.count ? e.result : [];
        control.allowServerDataBinding = false;
        this.processJsonData(viewData, control, Object.keys(viewData).length, series);
        this.seriesLength += series ? 1 : this.seriesLength;
        if (!series || this.seriesLength === control.series.length) {
            this.processXAxis(control);
            this.calculateGroupingBounds(control);
            this.processYAxis(control);
            control.renderChart();
        }
    }
    /**
     * Process JSON data from data source.
     *
     * @param {Object[]} viewData - The data array to be processed.
     * @param {RangeNavigator} control - The RangeNavigator control.
     * @param {number} len - The length of the data array.
     * @param {RangeNavigatorSeries} series - The series data for the RangeNavigator control.
     * @returns {void}
     */
    processJsonData(viewData, control, len, series) {
        let i = 0;
        let point;
        const xName = (series && series.xName) || control.xName;
        const yName = (series && series.yName) || control.yName;
        const controlValueType = control.valueType;
        while (i < len) {
            point = new DataPoint(getValue(xName, viewData[i]), getValue(yName, viewData[i]));
            point.index = i;
            point.yValue = +point.y;
            point.symbolLocations = [];
            point.regions = [];
            if (controlValueType.indexOf('DateTime') > -1) {
                const dateParser = control.intl.getDateParser({ skeleton: 'full', type: 'dateTime' });
                const dateFormatter = control.intl.getDateFormat({ skeleton: 'full', type: 'dateTime' });
                point.x = new Date(DataUtil.parse.parseJson({ val: point.x }).val);
                point.xValue = Date.parse(dateParser(dateFormatter(point.x)));
                if (controlValueType === 'DateTimeCategory') {
                    if (this.indexLabels[point.xValue.toString()] === undefined) {
                        this.indexLabels[point.xValue.toString()] = this.labels.length;
                        this.labels.push(point.xValue.toString());
                    }
                    point.xValue = this.indexLabels[point.xValue.toString()];
                }
            }
            else {
                // Double / Logarithmic
                point.xValue = +point.x;
            }
            // Ignore invalid numeric points for Double / Logarithmic so
            // axis range/label generation does not receive NaN/undefined.
            if (controlValueType === 'Double' || controlValueType === 'Logarithmic') {
                if (isNullOrUndefined(point.xValue) ||
                    isNullOrUndefined(point.yValue) ||
                    isNaN(point.xValue) ||
                    isNaN(point.yValue)) {
                    i++;
                    continue;
                }
                if (controlValueType === 'Logarithmic' && point.xValue <= 0) {
                    i++;
                    continue;
                }
            }
            if (series) {
                series.points.push(point);
            }
            this.xMin = Math.min(this.xMin, point.xValue);
            this.xMax = Math.max(this.xMax, point.xValue);
            if (point.visible) {
                this.yMin = Math.min(this.yMin, point.yValue);
                this.yMax = Math.max(this.yMax, point.yValue);
            }
            control.rangeSlider.points.push(point);
            i++;
        }
    }
    /**
     * Process x axis for range navigator.
     *
     * @private
     * @param {RangeNavigator} control - The RangeNavigator control.
     * @returns {void}
     */
    processXAxis(control) {
        const axis = {
            minimum: control.minimum,
            maximum: control.maximum,
            interval: control.interval,
            valueType: control.valueType,
            isInversed: control.enableRtl,
            labelFormat: control.labelFormat,
            logBase: control.logBase,
            skeleton: control.skeleton,
            skeletonType: control.skeletonType
        };
        this.xAxis = axis;
        this.xAxis.intervalType = control.intervalType;
        this.xAxis.maximumLabels = 3;
        this.xAxis.skeleton = control.skeleton;
        this.xAxis.intervalDivs = [10, 5, 2, 1];
        this.xAxis.rect = control.bounds;
        this.xAxis.visibleLabels = [];
        this.xAxis.orientation = 'Horizontal';
        this.xAxis.labels = this.labels;
        this.xAxis.indexLabels = this.indexLabels;
        const axisModule = control[firstToLowerCase(control.valueType) + 'Module'];
        axisModule.min = this.xMin;
        axisModule.max = this.xMax;
        axisModule.getActualRange(this.xAxis, control.bounds);
        const axisValueType = this.xAxis.valueType;
        if (axisValueType === 'Double' || axisValueType === 'DateTime' || axisValueType === 'DateTimeCategory') {
            axisModule.updateActualRange(this.xAxis, this.xAxis.actualRange.min, this.xAxis.actualRange.max, this.xAxis.actualRange.interval);
        }
        // add half-point padding on X-axis so first and last columns
        // are fully visible instead of being clipped by the navigator bounds.
        const hasColumnSeries = !control.stockChart && !!control.series && control.series.some((s) => s.type === 'Column');
        if (hasColumnSeries && axisValueType !== 'Logarithmic') {
            let minDelta = Infinity;
            control.series.forEach((series) => {
                if (series.type === 'Column' && series.points && series.points.length > 1) {
                    const sortedPoints = series.points
                        .filter((p) => p.visible !== false)
                        .slice()
                        .sort((a, b) => a.xValue - b.xValue);
                    for (let i = 1; i < sortedPoints.length; i++) {
                        const delta = sortedPoints[i].xValue -
                            sortedPoints[i - 1].xValue;
                        if (delta > 0) {
                            minDelta = Math.min(minDelta, delta);
                        }
                    }
                }
            });
            let padding = 0;
            if (axisValueType === 'DateTimeCategory') {
                padding = 0.5;
            }
            else if (isFinite(minDelta)) {
                padding = minDelta / 2;
            }
            else {
                // fallback for single-point or equal-x cases
                padding = (this.xAxis.actualRange.delta || 1) / 2;
            }
            // apply padding only when min/max are not explicitly set by user
            if (isNullOrUndefined(control.minimum)) {
                this.xAxis.actualRange.min -= padding;
            }
            if (isNullOrUndefined(control.maximum)) {
                this.xAxis.actualRange.max += padding;
            }
        }
        this.xAxis.actualRange.delta = this.xAxis.actualRange.max - this.xAxis.actualRange.min;
        this.xAxis.visibleRange = this.xAxis.actualRange;
        axisModule.calculateVisibleLabels(this.xAxis, control);
        if (this.xAxis.valueType === 'DateTimeCategory' && control.periodSelectorModule) {
            control.periodSelectorModule.isDatetimeCategory = true;
            control.periodSelectorModule.sortedData = this.labels.map((label) => parseInt(label, 10));
        }
    }
    /**
     * Process yAxis for range navigator.
     *
     * @param {RangeNavigator} control - RangeNavigator instance.
     * @private
     * @returns {void}
     */
    processYAxis(control) {
        const axis = {
            majorGridLines: { width: 0 }, rangePadding: 'None',
            majorTickLines: { width: 0 }, labelStyle: { size: '0' },
            visible: false, valueType: 'Double', minimum: null, maximum: null,
            interval: null
        };
        this.yAxis = axis;
        this.yAxis.rect = control.bounds;
        this.yAxis.maximumLabels = 3;
        this.yAxis.intervalDivs = [10, 5, 2, 1];
        this.yAxis.orientation = 'Vertical';
        let yMin = this.yMin;
        let yMax = this.yMax;
        if (yMin === yMax) {
            yMax += 1;
            yMin -= 1;
        }
        const hasColumnSeries = !!control.series &&
            control.series.some((s) => s.type === 'Column');
        if (hasColumnSeries) {
            const delta = yMax - yMin;
            const padding = delta === 0 ? Math.abs((yMax || 1)) * 0.1 : delta * 0.1;
            if (yMin >= 0) {
                // Positive-only data:
                // add some space below min so the smallest column is visible
                yMin = Math.max(0, yMin - padding);
            }
            else if (yMax <= 0) {
                // Negative-only data:
                // add some space above max and below min
                yMax = Math.min(0, yMax + padding);
                yMin = yMin - padding;
            }
            else {
                // Mixed positive/negative:
                // pad both sides
                yMin = yMin - padding;
                yMax = yMax + padding;
            }
        }
        control.doubleModule.min = yMin;
        control.doubleModule.max = yMax;
        control.doubleModule.getActualRange(this.yAxis, control.bounds);
        control.doubleModule.updateActualRange(this.yAxis, this.yAxis.actualRange.min, this.yAxis.actualRange.max, this.yAxis.actualRange.interval);
        this.yAxis.actualRange.delta = this.yAxis.actualRange.max - this.yAxis.actualRange.min;
        this.yAxis.visibleRange = this.yAxis.actualRange;
    }
    /**
     * Process Light weight control.
     *
     * @param {RangeNavigator} control - RangeNavigator instance.
     * @private
     * @returns {void}
     */
    renderSeries(control) {
        this.chartGroup = control.renderer.createGroup({ id: control.element.id + '_chart' });
        const colors = getSeriesColor(control.theme);
        const allSeries = control.series;
        const visibleColumnSeries = control.stockChart ? [] : allSeries.filter((s, idx) => {
            const stockSeriesVisible = control.stockChart ? control.stockChart.series[idx].visible : true;
            return s.type === 'Column' && stockSeriesVisible;
        });
        // Precompute side-by-side positions ONCE for all visible
        if (visibleColumnSeries.length > 1) {
            const controlAny = control; // used any since we didn't have the chart properties in range navigator series
            controlAny.enableSideBySidePlacement = true;
            controlAny.visibleSeries = visibleColumnSeries;
            controlAny.columns = controlAny.columns || [{}];
            controlAny.rows = controlAny.rows || [{}];
            visibleColumnSeries.forEach((columnSeries) => {
                columnSeries.chart = control;
                columnSeries.xAxis = this.xAxis;
                columnSeries.yAxis = this.yAxis;
                columnSeries.findSeriesCollection = function () {
                    return visibleColumnSeries;
                };
                columnSeries.position = null;
                columnSeries.rectCount = null;
            });
            const columnModule = controlAny['columnSeriesModule'];
            if (columnModule && columnModule.getSideBySidePositions && visibleColumnSeries[0]) {
                columnModule.getSideBySidePositions(visibleColumnSeries[0]);
            }
        }
        allSeries.map((series, index) => {
            const isSeriesVisible = control.stockChart ? control.stockChart.series[index].visible : true;
            if (isSeriesVisible) {
                series.xAxis = this.xAxis;
                series.yAxis = this.yAxis;
                series.chart = control;
                series.index = index;
                series.xAxis.isInversed = control.enableRtl;
                const stockSeries = control.stockChart && control.stockChart.series && control.stockChart.series[index];
                const hasGradient = (stockSeries && !isNullOrUndefined(stockSeries.linearGradient) &&
                    stockSeries.linearGradient.gradientColorStop.length > 0) ||
                    (stockSeries && !isNullOrUndefined(stockSeries.radialGradient) &&
                        stockSeries.radialGradient.gradientColorStop.length > 0);
                const fillColor = (hasGradient && series.interior.indexOf('gradient')) > 0
                    ? series.interior
                    : (series.fill || colors[index % colors.length]);
                series.interior = fillColor;
                this.createSeriesElement(control, series, index);
                const seriesAny = series;
                const controlAny = control;
                seriesAny.emptyPointSettings = { mode: 'Zero' };
                seriesAny.drawPoints = series.points;
                seriesAny.category = seriesAny.category || 'Series';
                seriesAny.seriesType = seriesAny.seriesType || 'XY';
                const seriesAxisValueType = series.xAxis.valueType;
                if (seriesAxisValueType === 'DateTimeCategory') {
                    for (let i = 0; i < series.points.length; i++) {
                        series.points[i].xValue =
                            this.xAxis.labels.indexOf(Date.parse(series.points[i].x.toString()).toString());
                    }
                }
                const originalSeriesType = series.type;
                const rangeNavigatorType = control.stockChart
                    ? (originalSeriesType === 'Area' ? 'Area' : originalSeriesType === 'StepLine' ? 'StepLine' : 'Line')
                    : originalSeriesType;
                const seriesType = rangeNavigatorType;
                // Spline / SplineArea setup
                if (seriesType === 'Spline' || seriesType === 'SplineArea') {
                    const originalYMin = seriesAny.yMin;
                    const originalYMax = seriesAny.yMax;
                    const validPoints = series.points.filter((p) => p.yValue != null);
                    if (validPoints.length > 0) {
                        const dataYValues = validPoints.map((p) => p.yValue);
                        const dataMin = Math.min(...dataYValues);
                        const dataMax = Math.max(...dataYValues);
                        seriesAny.yMin = dataMin;
                        seriesAny.yMax = dataMax;
                        const splineModuleName = firstToLowerCase(seriesType) + 'SeriesModule';
                        const splineModule = controlAny[splineModuleName];
                        if (splineModule && splineModule.findSplinePoint) {
                            splineModule.chart = controlAny;
                            splineModule.findSplinePoint(series);
                        }
                        seriesAny.yMin = originalYMin;
                        seriesAny.yMax = originalYMax;
                    }
                    seriesAny.cardinalSplineTension = 0.5;
                    seriesAny.splineType = 'Natural';
                }
                // Column setup
                if (seriesType === 'Column') {
                    controlAny.visibleSeries = visibleColumnSeries;
                    controlAny.requireInvertedAxis = false;
                    controlAny.enableCanvas = false;
                    controlAny.duration = control.animationDuration;
                    controlAny.redraw = control.redraw;
                    seriesAny.columnSpacing = isNullOrUndefined(seriesAny.columnSpacing) ? 0.1 : seriesAny.columnSpacing;
                    seriesAny.columnWidth = isNullOrUndefined(seriesAny.columnWidth) ? 0.7 : seriesAny.columnWidth;
                    seriesAny.clipRect = series.clipRect;
                    seriesAny.isRectSeries = true;
                    if (visibleColumnSeries.length > 1) {
                        const columnIndex = visibleColumnSeries.indexOf(series);
                        controlAny.enableSideBySidePlacement = true;
                        seriesAny.position = columnIndex;
                        seriesAny.rectCount = visibleColumnSeries.length;
                    }
                    else {
                        // Single series
                        seriesAny.position = 0;
                        seriesAny.rectCount = 0;
                        controlAny.enableSideBySidePlacement = false;
                    }
                    // Ensure point metadata exists
                    for (let cp = 0; cp < series.points.length; cp++) {
                        series.points[cp].index = cp;
                        if (isNullOrUndefined(series.points[cp].visible)) {
                            series.points[cp].visible =
                                !isNullOrUndefined(series.points[cp].yValue) &&
                                    !isNaN(series.points[cp].yValue);
                        }
                        series.points[cp].symbolLocations =
                            series.points[cp].symbolLocations;
                        series.points[cp].regions =
                            series.points[cp].regions;
                    }
                    const validColumnPoints = series.points.filter((p) => p.visible);
                    let minPixelDelta = Infinity;
                    let prevX = null;
                    for (let px = 0; px < validColumnPoints.length; px++) {
                        const pointX = getPoint(validColumnPoints[px].xValue, 0, this.xAxis, this.yAxis, false).x;
                        if (prevX !== null) {
                            const currentDelta = Math.abs(pointX - prevX);
                            if (currentDelta > 0) {
                                minPixelDelta = Math.min(minPixelDelta, currentDelta);
                            }
                        }
                        prevX = pointX;
                    }
                    if (!isFinite(minPixelDelta) || minPixelDelta <= 0) {
                        if (validColumnPoints.length === 1) {
                            minPixelDelta = this.xAxis.rect.width * 0.15;
                        }
                        else if (validColumnPoints.length > 1) {
                            minPixelDelta = this.xAxis.rect.width / validColumnPoints.length;
                        }
                        else {
                            minPixelDelta = 10;
                        }
                    }
                    let basePixelWidth = minPixelDelta * seriesAny.columnWidth;
                    // Split available slot among visible column series
                    if (visibleColumnSeries.length > 1) {
                        basePixelWidth = basePixelWidth / visibleColumnSeries.length;
                    }
                    seriesAny.columnWidthInPixel = Math.max(basePixelWidth, 1);
                    if (!seriesAny.border) {
                        seriesAny.border = { width: 0, color: 'transparent' };
                    }
                }
                const seriesModuleName = firstToLowerCase(seriesType) + 'SeriesModule';
                const seriesModule = controlAny[seriesModuleName];
                if (seriesModule) {
                    seriesModule.render(series, this.xAxis, this.yAxis, false);
                }
                else {
                    const defaultSeriesModule = controlAny['lineSeriesModule'];
                    defaultSeriesModule.render(series, this.xAxis, this.yAxis, false);
                }
                this.chartGroup.appendChild(series.seriesElement);
                // Animation
                if (((series.animation.enable && animationMode !== 'Disable') || animationMode === 'Enable') &&
                    control.animateSeries) {
                    if (seriesModule) {
                        seriesModule.chart = controlAny;
                        seriesModule.doAnimation(series);
                    }
                }
                series.type = originalSeriesType;
            }
        });
    }
    /**
     * Append series elements in element.
     *
     * @param {RangeNavigator} control - The RangeNavigator control.
     * @returns {void}
     */
    appendSeriesElements(control) {
        control.svgObject.appendChild(this.chartGroup);
        if (control.series.length) {
            this.drawSeriesBorder(control);
        }
    }
    createSeriesElement(control, series, index) {
        const elementId = control.element.id;
        series.clipRect = new Rect(this.xAxis.rect.x, this.yAxis.rect.y, this.xAxis.rect.width, this.yAxis.rect.height);
        series.clipRectElement = control.renderer.drawClipPath(new RectOption(elementId + '_RangeSeriesClipRect_' + index, 'transparent', { width: 1, color: 'Gray' }, 1, {
            x: 0, y: 0,
            width: series.clipRect.width,
            height: series.clipRect.height
        }));
        series.seriesElement = control.renderer.createGroup({
            'id': elementId + 'SeriesGroup' + index,
            'transform': 'translate(' + series.clipRect.x + ',' + (series.clipRect.y) + ')',
            'clip-path': 'url(#' + elementId + '_RangeSeriesClipRect_' + index + ')'
        });
        series.seriesElement.appendChild(series.clipRectElement);
    }
    /**
     * Calculate grouping bounds for x axis.
     *
     * @private
     * @param {RangeNavigator} control - The RangeNavigator control.
     * @returns {void}
     */
    calculateGroupingBounds(control) {
        const padding = control.margin.bottom;
        const labelHeight = measureText('string', control.labelStyle, control.themeStyle.axisLabelFont).height;
        const controlValueType = control.valueType;
        const xMin = controlValueType === 'DateTimeCategory' ? parseInt(this.xAxis.labels[this.xMin], 10) : this.xMin;
        const xMax = controlValueType === 'DateTimeCategory' ? parseInt(this.xAxis.labels[this.xMax], 10) : this.xMax;
        this.calculateDateTimeNiceInterval(this.xAxis, new Size(control.bounds.width, control.bounds.height), xMin, xMax, false);
        if (control.enableGrouping && (controlValueType === 'DateTime' || controlValueType === 'DateTimeCategory') &&
            (this.xAxis.actualIntervalType !== 'Years' || !control.series.length)) {
            control.bounds.height -= (control.labelPosition === 'Outside' || !control.series.length) ? padding + labelHeight : (labelHeight + 2 * padding);
        }
        if (!control.series.length) {
            control.bounds.y += control.bounds.height / 4;
            control.bounds.height = control.bounds.height / 2;
        }
    }
    drawSeriesBorder(control) {
        const start = control.stockChart ? 'M' : 'L';
        const close = control.stockChart ? '' : 'Z';
        const options = new PathOption(control.element.id + '_SeriesBorder', 'transparent', control.navigatorBorder.width, control.navigatorBorder.color || (control.theme.indexOf('Dark') > -1 ? '#49454F' : '#DDDDDD'), 1, control.navigatorBorder.dashArray, ('M ' + (control.bounds.x) + ' ' + (control.bounds.y) +
            ' L ' + (control.bounds.x + control.bounds.width) + ' ' + control.bounds.y +
            start + (control.bounds.x + control.bounds.width) + ' ' + (control.bounds.y + control.bounds.height) +
            ' L ' + (control.bounds.x) + ' ' + (control.bounds.y + control.bounds.height) + close));
        const htmlObject = control.renderer.drawPath(options);
        control.svgObject.appendChild(htmlObject);
    }
}
